import React, { useEffect, useState, useCallback } from "react";
import {
  Button, Card, Form, Input, InputNumber, Space, Tag, Select,
  message, Tabs, Tooltip, Spin, Progress
} from "antd";
import {
  FaCheck, FaArrowLeft, FaPlus, FaTrash,
  FaRobot
} from "react-icons/fa";
import { SettingOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import axiosHttp from "../../appRedux/services/axiosCommon";
import Page from "../../components/Common/Page";
import AIGeneratorConfig from "../../components/Common/AIGeneratorConfig";

const { Option } = Select;
const { TextArea } = Input;

export default function PlatformPageBuilder() {
  const { platformId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [platformData, setPlatformData] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [allPlatforms, setAllPlatforms] = useState([]);

  // AI & Exa state
  const [generatingSection, setGeneratingSection] = useState({});
  const [runningGlobalExa, setRunningGlobalExa] = useState(false);
  const [globalProgress, setGlobalProgress] = useState(null);
  const [configModal, setConfigModal] = useState({
    visible: false,
    category: "Platform Page Builder",
    defaultPrompt: "",
    contextInstructions: "",
    hidePromptsTab: false
  });

  // Fetch Platform, Tags & All Platforms for competitor dropdowns
  const fetchPlatformDetails = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch Platform
      const res = await axiosHttp.get(`/v1/admin/platforms/${platformId}`).catch(async () => {
        // Fallback to code query if id is a slug/code
        return await axiosHttp.get(`/v1/admin/platforms/code/${platformId}`);
      });
      const data = res?.data?.results || res?.data?.data || res?.data;
      if (!data) throw new Error("Platform not found");
      setPlatformData(data);

      const ext = data.extendedMetadata || {};
      const platformName = data.name || "Platform";

      // Extract pros from proprietaryFeatures or defaults
      let defaultPros = Array.isArray(data.prosAndCons?.pros) && data.prosAndCons.pros.length > 0
        ? data.prosAndCons.pros
        : (Array.isArray(ext.proprietaryFeatures) && ext.proprietaryFeatures.length > 0
            ? ext.proprietaryFeatures.slice(0, 4)
            : [
                `Comprehensive catalog covering in-demand career skills and modern tools`,
                `Flexible self-paced schedule with on-demand video lessons and exercises`,
                `Recognized certificates of completion for portfolio and resume validation`
              ]);

      let defaultCons = Array.isArray(data.prosAndCons?.cons) && data.prosAndCons.cons.length > 0
        ? data.prosAndCons.cons
        : [
            `Free audit or preview mode has restrictions on graded assessments`,
            `Course quality can vary between individual instructors and older course versions`,
            `Certificates may not carry academic university credit unless enrolled in specialized degree tracks`
          ];

      let defaultCredentialNotes = Array.isArray(ext.credentialTypes) && ext.credentialTypes.length > 0
        ? ext.credentialTypes.join("; ")
        : "Certificates of completion and skill assessment badges.";

      // Hydrate form with deep database extraction
      form.setFieldsValue({
        name: data.name || "",
        code: data.code || "",
        tagline: data.tagline || ext.tagline || `${platformName} Online Learning & Certification Platform`,
        subtitle: data.subtitle || ext.subtitle || data.description || "",
        description: data.description || ext.description || ext.institutionalBackground || "",
        heroTitle: data.heroTitle || `${platformName} Review 2026: Cost, Value & Is It Worth It?`,
        heroSubtitle: data.heroSubtitle || data.description || ext.institutionalBackground || "",
        website: data.website || ext.website || "",
        logoUrl: data.logoUrl || ext.logoUrl || "",
        brandColor: data.brandColor || "#4f46e5",
        status: data.status || "published",
        isActive: data.isActive !== false,
        tags: Array.isArray(data.tags)
          ? data.tags.map(t => (typeof t === "object" ? t._id : t))
          : [],
        verdict: {
          score: data.verdict?.score ?? 8.5,
          summary: data.verdict?.summary || data.tagline || ext.tagline || `Strong all-around platform for structured learning and professional upskilling.`,
          editorNote: data.verdict?.editorNote || ext.institutionalBackground || data.description || `${platformName} provides high-quality curriculum designed to prepare learners for real-world industry demands.`,
          bottomLine: data.verdict?.bottomLine || ext.catalogScale || `If your goal is structured skill acquisition, ${platformName} is well worth considering.`,
        },
        prosAndCons: {
          pros: defaultPros,
          cons: defaultCons,
          theCatch: data.prosAndCons?.theCatch || "While initial previews are available, full graded feedback and verifiable certificates require an active subscription or course purchase.",
        },
        learnerFitProfiles: Array.isArray(data.learnerFitProfiles) && data.learnerFitProfiles.length > 0
          ? data.learnerFitProfiles
          : [
              { persona: "Career Changers", fitScore: 9, reasoning: "Provides structured job-ready certification tracks and portfolio projects." },
              { persona: "Working Professionals", fitScore: 8, reasoning: "Flexible self-paced schedule for evening and weekend learning." },
              { persona: "University Students", fitScore: 7, reasoning: "Great supplement to academic coursework and theoretical foundations." },
              { persona: "Casual Hobbyists", fitScore: 5, reasoning: "May be overkill if you only want casual single lessons without testing." }
            ],
        costAnalysis: {
          freeTierReality: data.costAnalysis?.freeTierReality || ext.pricingModel || "Free preview and audit modes available for selected lessons; assignments require paid access.",
          bestValuePlan: data.costAnalysis?.bestValuePlan || "Annual subscription provides the lowest cost-per-month if taking 2+ courses.",
          breakEvenNotes: data.costAnalysis?.breakEvenNotes || "Completing 2 courses or specializations per year typically breaks even with annual membership pricing.",
          hiddenCosts: data.costAnalysis?.hiddenCosts || "Subscriptions auto-renew periodically. Check refund terms (typically 7–14 days from purchase).",
        },
        certificateValue: {
          industryRecognition: data.certificateValue?.industryRecognition || defaultCredentialNotes,
          employerAcceptance: data.certificateValue?.employerAcceptance || "Respected for practical skill demonstration and technical portfolio validation during interviews.",
          linkedInDisplayValue: data.certificateValue?.linkedInDisplayValue || "Direct 1-click verification link to LinkedIn Licenses & Certifications section.",
        },
        competitorComparisons: Array.isArray(data.competitorComparisons) && data.competitorComparisons.length > 0
          ? data.competitorComparisons
          : [
              {
                competitorCode: "udemy",
                chooseIfThis: `You prefer structured curriculum paths and validated certificates.`,
                chooseIfOther: `You want a cheap $15 one-off tutorial for a very specific tool or library.`
              }
            ],
        faqs: Array.isArray(data.faqs) && data.faqs.length > 0
          ? data.faqs
          : [
              { question: `Is ${platformName} accredited?`, answer: defaultCredentialNotes, priority: 1, isActive: true },
              { question: `What is the refund policy?`, answer: "Most plans offer a 7 to 14-day refund window from purchase.", priority: 2, isActive: true }
            ],
        seo: {
          metaTitle: data.seo?.metaTitle || `${platformName} Review 2026: Cost, Value & Is It Worth It?`,
          metaDescription: data.seo?.metaDescription || `In-depth review of ${platformName}. Compare subscription costs, certificate recognition, and pros & cons before purchasing.`,
          ogTitle: data.seo?.ogTitle || `${platformName} Honest Review & Decision Guide`,
          ogDescription: data.seo?.ogDescription || `Everything you need to know about ${platformName} before buying.`,
          keywords: Array.isArray(data.seo?.keywords) && data.seo.keywords.length > 0
            ? data.seo.keywords
            : [`${platformName.toLowerCase()} review`, `${platformName.toLowerCase()} pricing`, `${platformName.toLowerCase()} certificates`, `is ${platformName.toLowerCase()} worth it`],
        }
      });

      // 2. Fetch Centralized Tags
      const tagsRes = await axiosHttp.get("/v3/admin/tags").catch(() => ({ data: { data: [] } }));
      const tagsList = tagsRes?.data?.data || tagsRes?.data?.results || [];
      setAvailableTags(tagsList);

      // 3. Fetch All Platforms for competitor options
      const platformsRes = await axiosHttp.get("/v1/admin/platforms?size=100").catch(() => ({ data: { data: [] } }));
      const pList = platformsRes?.data?.data?.data || platformsRes?.data?.results || [];
      setAllPlatforms(pList.filter(p => (p._id || p.id) !== data._id));

    } catch (err) {
      console.error("Failed to load platform page builder data:", err);
      message.error("Failed to load platform page data.");
    } finally {
      setLoading(false);
    }
  }, [platformId, form]);

  useEffect(() => {
    if (platformId) {
      fetchPlatformDetails();
    }
  }, [platformId, fetchPlatformDetails]);

  // Save changes
  const handleSave = async (statusOverride = null) => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        ...values,
        status: statusOverride || values.status || platformData?.status || "published",
        verdict: {
          ...values.verdict,
          score: Number(values.verdict?.score) || null,
        },
        learnerFitProfiles: (values.learnerFitProfiles || []).map(p => ({
          persona: p.persona,
          fitScore: Number(p.fitScore) || 5,
          reasoning: p.reasoning || "",
        })),
        faqs: (values.faqs || []).map(f => ({
          question: f.question,
          answer: f.answer,
          priority: Number(f.priority) || 0,
          isActive: f.isActive !== false,
        })),
      };

      const targetId = platformData?._id || platformId;
      await axiosHttp.put(`/v1/admin/platforms/${targetId}`, payload);
      message.success("Platform page successfully saved!");
      setPlatformData(prev => ({ ...prev, ...payload }));
    } catch (err) {
      console.error("Error saving platform:", err);
      message.error("Failed to save changes. Please check required fields.");
    } finally {
      setSaving(false);
    }
  };

  // Section AI Generation triggers
  const handleGenerateSection = async (sectionKey) => {
    const platformName = form.getFieldValue("name") || platformData?.name || "the platform";
    const platformCode = form.getFieldValue("code") || platformData?.code || "";

    setGeneratingSection(prev => ({ ...prev, [sectionKey]: true }));

    try {
      let prompt = "";
      let instructions = "";

      if (sectionKey === "verdict") {
        prompt = `You are a critical, honest education analyst for ChoiceDekho. Analyze ${platformName} (${platformCode}). Provide an objective rating score from 0.0 to 10.0, a 1-sentence decision hook, a 2-paragraph honest editor's review, and a concise 1-sentence bottom line.`;
        instructions = `Return JSON with keys: score (number e.g. 8.7), summary (string), editorNote (string), bottomLine (string).`;
      } else if (sectionKey === "prosAndCons") {
        prompt = `Analyze ${platformName}. Provide 3 distinct genuine strengths, 3 critical drawbacks/complaints, and "theCatch" (the single biggest hidden restriction or monetization catch that marketing hides from students).`;
        instructions = `Return JSON with keys: pros (array of 3 strings), cons (array of 3 strings), theCatch (string).`;
      } else if (sectionKey === "learnerFit") {
        prompt = `Evaluate which learner personas ${platformName} is best and worst for. Rate each out of 10 with clear reasoning: Career Changers, Working Professionals, University Students, Casual Hobbyists.`;
        instructions = `Return JSON with key 'learnerFitProfiles' containing array of objects with keys: persona, fitScore (1-10), reasoning.`;
      } else if (sectionKey === "costAnalysis") {
        prompt = `Analyze the pricing and economics of ${platformName}. Explain the reality of its free tier, its best-value subscription plan, the break-even course count, and any hidden fees.`;
        instructions = `Return JSON with keys: freeTierReality, bestValuePlan, breakEvenNotes, hiddenCosts.`;
      } else if (sectionKey === "certificateValue") {
        prompt = `Evaluate the true employer and industry value of certificates from ${platformName}. Assess industry recognition, hiring manager acceptance, and LinkedIn profile value.`;
        instructions = `Return JSON with keys: industryRecognition, employerAcceptance, linkedInDisplayValue.`;
      } else if (sectionKey === "seo") {
        prompt = `Generate SEO meta tags for ${platformName} decision guide on ChoiceDekho. Provide a click-worthy title, meta description (under 155 chars), OpenGraph tags, and 5 search keywords.`;
        instructions = `Return JSON with keys: metaTitle, metaDescription, ogTitle, ogDescription, keywords (array of strings).`;
      }

      const res = await axiosHttp.post("/v1/admin/ai/proxy", {
        prompt,
        instructions,
        systemPrompt: "You are an expert online learning platform reviewer for ChoiceDekho. Always return pure, valid JSON."
      });

      const generated = res?.data?.data || res?.data?.result || res?.data;

      if (generated) {
        if (sectionKey === "verdict") {
          form.setFieldsValue({ verdict: { ...form.getFieldValue("verdict"), ...generated } });
        } else if (sectionKey === "prosAndCons") {
          form.setFieldsValue({ prosAndCons: { ...form.getFieldValue("prosAndCons"), ...generated } });
        } else if (sectionKey === "learnerFit") {
          form.setFieldsValue({ learnerFitProfiles: generated.learnerFitProfiles || generated });
        } else if (sectionKey === "costAnalysis") {
          form.setFieldsValue({ costAnalysis: { ...form.getFieldValue("costAnalysis"), ...generated } });
        } else if (sectionKey === "certificateValue") {
          form.setFieldsValue({ certificateValue: { ...form.getFieldValue("certificateValue"), ...generated } });
        } else if (sectionKey === "seo") {
          form.setFieldsValue({ seo: { ...form.getFieldValue("seo"), ...generated } });
        }
        message.success(`Generated ${sectionKey} successfully!`);
      }
    } catch (err) {
      console.error(`AI generation failed for ${sectionKey}:`, err);
      message.error(`AI generation failed for ${sectionKey}.`);
    } finally {
      setGeneratingSection(prev => ({ ...prev, [sectionKey]: false }));
    }
  };

  // Global AutoFill using Exa + AI
  const handleGlobalAutoFill = async () => {
    const platformName = form.getFieldValue("name") || platformData?.name || "Platform";
    setRunningGlobalExa(true);
    setGlobalProgress({ current: 1, total: 6, section: "Live Web Research (Exa)..." });

    try {
      // 1. Live Exa Web Search
      await axiosHttp.post("/v1/admin/exa/search", {
        query: `${platformName} online learning platform review pricing pros cons certificates 2026`,
        numResults: 5,
      }).catch(() => null);

      // 2. Step-by-step AI section generations
      const sections = ["verdict", "prosAndCons", "learnerFit", "costAnalysis", "certificateValue", "seo"];
      for (let i = 0; i < sections.length; i++) {
        setGlobalProgress({ current: i + 1, total: sections.length, section: `Synthesizing ${sections[i]}...` });
        await handleGenerateSection(sections[i]);
      }

      message.success(`Full page AutoFill complete for ${platformName}! Review and click Save.`);
    } catch (err) {
      console.error("Global AutoFill failed:", err);
      message.error("Global AutoFill encountered an error.");
    } finally {
      setRunningGlobalExa(false);
      setGlobalProgress(null);
    }
  };

  if (loading) {
    return (
      <Page title="Platform PageBuilder">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
          <Spin size="large" tip="Loading Platform PageBuilder..." />
        </div>
      </Page>
    );
  }

  const brandColor = form.getFieldValue("brandColor") || platformData?.brandColor || "#4f46e5";
  const platformInitials = (platformData?.name || "P").substring(0, 2).toUpperCase();

  return (
    <Page title={`${platformData?.name || "Platform"} - PageBuilder`}>
      {/* Sticky Action Header */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 99,
          background: "#fff",
          borderBottom: "1px solid #e2e8f0",
          padding: "12px 24px",
          marginBottom: 20,
          boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
        }}
      >
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          {/* Left Title & Breadcrumb */}
          <div className="d-flex align-items-center gap-3">
            <Button
              icon={<FaArrowLeft />}
              onClick={() => navigate("/platforms")}
              size="middle"
              style={{ borderRadius: "6px" }}
            />
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                backgroundColor: brandColor,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "14px",
              }}
            >
              {platformInitials}
            </div>
            <div>
              <div className="d-flex align-items-center gap-2">
                <h4 style={{ margin: 0, fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>
                  {platformData?.name}
                </h4>
                <Tag color="purple" style={{ margin: 0, borderRadius: "4px", fontSize: "11px", fontWeight: 600 }}>
                  {platformData?.code}
                </Tag>
                <Tag
                  color={platformData?.status === "published" ? "success" : "warning"}
                  style={{ margin: 0, borderRadius: "4px", fontWeight: 600, fontSize: "11px" }}
                >
                  {platformData?.status === "published" ? "Published" : "Draft"}
                </Tag>
              </div>
              <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                Decision Guidance & Editorial Studio
              </div>
            </div>
          </div>

          {/* Right Action Controls */}
          <Space size="small">
            <Tooltip title="AutoFill all editorial sections using Exa Web Search + Cloudflare AI">
              <Button
                type="dashed"
                icon={<ThunderboltOutlined style={{ color: "#1890ff" }} />}
                loading={runningGlobalExa}
                onClick={handleGlobalAutoFill}
                style={{ borderColor: "#1890ff", color: "#1890ff", fontWeight: 600 }}
              >
                AutoFill (Exa + AI)
              </Button>
            </Tooltip>

            <Tooltip title="AI Prompt & Generation Settings">
              <Button
                icon={<SettingOutlined />}
                onClick={() => setConfigModal(prev => ({ ...prev, visible: true }))}
              />
            </Tooltip>

            <div style={{ width: 1, height: 24, background: "#e2e8f0", margin: "0 4px" }} />

            {platformData?.status === "draft" && (
              <Button
                type="primary"
                style={{ backgroundColor: "#10b981", borderColor: "#10b981" }}
                onClick={() => handleSave("published")}
                icon={<FaCheck />}
                loading={saving}
              >
                Publish Live
              </Button>
            )}

            <Button
              type="primary"
              loading={saving}
              onClick={() => handleSave()}
              style={{ minWidth: 110, fontWeight: 600 }}
            >
              Save Changes
            </Button>
          </Space>
        </div>

        {/* Global Progress Bar */}
        {globalProgress && (
          <div style={{ marginTop: "10px" }}>
            <Progress
              percent={Math.round((globalProgress.current / globalProgress.total) * 100)}
              status="active"
              strokeColor={{ from: "#10b981", to: "#3b82f6" }}
              size="small"
            />
            <div style={{ fontSize: "11px", color: "#047857", marginTop: "2px", fontWeight: 500 }}>
              {globalProgress.section} (Step {globalProgress.current}/{globalProgress.total})
            </div>
          </div>
        )}
      </div>

      {/* Main Workspace Body */}
      <div className="container-fluid" style={{ maxWidth: 1200, margin: "0 auto", paddingBottom: "60px" }}>
        <Form form={form} layout="vertical">
          <Card
            style={{ borderRadius: "12px", borderColor: "#e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.03)" }}
            bodyStyle={{ padding: "8px 24px 24px" }}
          >
            <Tabs defaultActiveKey="1" size="large" tabBarGutter={24}>
              
              {/* TAB 1: Identity & Hero */}
              <Tabs.TabPane
                tab={<span style={{ fontWeight: 600 }}><i className="fa fa-id-card me-1" /> Identity & Hero</span>}
                key="1"
              >
                <div style={{ paddingTop: 16 }}>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <Form.Item label="Platform Name" name="name" rules={[{ required: true }]}>
                        <Input size="large" />
                      </Form.Item>
                    </div>
                    <div className="col-md-4">
                      <Form.Item label="Platform Code" name="code" rules={[{ required: true }]}>
                        <Input size="large" />
                      </Form.Item>
                    </div>
                    <div className="col-md-4">
                      <Form.Item label="Brand Accent Color" name="brandColor">
                        <Input
                          size="large"
                          prefix={
                            <span
                              style={{
                                width: 14,
                                height: 14,
                                borderRadius: "50%",
                                backgroundColor: form.getFieldValue("brandColor") || "#4f46e5",
                                display: "inline-block",
                                marginRight: 4,
                              }}
                            />
                          }
                        />
                      </Form.Item>
                    </div>

                    <div className="col-md-6">
                      <Form.Item label="Page Hero Title (H1)" name="heroTitle">
                        <Input size="large" placeholder="e.g. Coursera Review 2026: Cost, Degrees & Verdict" />
                      </Form.Item>
                    </div>
                    <div className="col-md-6">
                      <Form.Item label="Official Website URL" name="website">
                        <Input size="large" placeholder="https://www.coursera.org" />
                      </Form.Item>
                    </div>

                    <div className="col-md-12">
                      <Form.Item label="Tagline / Short Pitch" name="tagline">
                        <Input size="large" placeholder="e.g. World-class learning from Stanford, Google, and Meta." />
                      </Form.Item>
                    </div>

                    <div className="col-md-12">
                      <Form.Item label="Description & Overview" name="description">
                        <TextArea rows={3} placeholder="Brief summary of the platform and course catalog..." />
                      </Form.Item>
                    </div>

                    {/* Centralized Decision Tags */}
                    <div className="col-md-12">
                      <Card size="small" style={{ borderRadius: 8, background: "#f8fafc", borderColor: "#e2e8f0" }}>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <span style={{ fontWeight: 600, fontSize: "13px", color: "#1e293b" }}>
                            <i className="la la-tags text-primary me-1" /> Decision & Comparison Tags (Centralized)
                          </span>
                          <span style={{ fontSize: "11px", color: "#64748b" }}>
                            Assigned tags power filterable badges and side-by-side comparison tables
                          </span>
                        </div>
                        <Form.Item name="tags" className="mb-0">
                          <Select
                            mode="multiple"
                            size="large"
                            placeholder="Select tags (Pricing, Format, Strengths, Perks...)"
                            optionFilterProp="children"
                            showSearch
                          >
                            {availableTags.map(tag => (
                              <Option key={tag._id} value={tag._id}>
                                <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                  {tag.color && (
                                    <span
                                      style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        backgroundColor: tag.color,
                                      }}
                                    />
                                  )}
                                  {tag.icon && <i className={tag.icon} style={{ fontSize: "12px" }} />}
                                  <span>{tag.name}</span>
                                  {tag.group && (
                                    <span style={{ fontSize: "10px", backgroundColor: "#e2e8f0", padding: "1px 5px", borderRadius: "3px" }}>
                                      {tag.group}
                                    </span>
                                  )}
                                </div>
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Card>
                    </div>
                  </div>
                </div>
              </Tabs.TabPane>

              {/* TAB 2: ChoiceDekho Editorial Verdict */}
              <Tabs.TabPane
                tab={<span style={{ fontWeight: 600 }}><i className="fa fa-star text-warning me-1" /> Verdict & Rating</span>}
                key="2"
              >
                <div style={{ paddingTop: 16 }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <h5 style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#1e293b" }}>
                        ChoiceDekho Verdict & Editorial Score
                      </h5>
                      <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                        Unbiased, critical evaluation and bottom-line verdict for learners
                      </p>
                    </div>
                    <Button
                      type="primary"
                      ghost
                      size="small"
                      icon={<FaRobot />}
                      loading={generatingSection.verdict}
                      onClick={() => handleGenerateSection("verdict")}
                    >
                      AI Generate Verdict
                    </Button>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-3">
                      <Card size="small" style={{ textAlign: "center", background: "#fdf8f6", borderColor: "#fed7aa" }}>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "#9a3412" }}>OVERALL SCORE</div>
                        <Form.Item name={["verdict", "score"]} noStyle>
                          <InputNumber
                            min={0}
                            max={10}
                            step={0.1}
                            style={{ fontSize: "28px", fontWeight: 800, width: "100%", textAlign: "center", marginTop: 8 }}
                          />
                        </Form.Item>
                        <div style={{ fontSize: "11px", color: "#ea580c", marginTop: 4 }}>out of 10.0</div>
                      </Card>
                    </div>

                    <div className="col-md-9">
                      <Form.Item label="1-Sentence Verdict Hook" name={["verdict", "summary"]}>
                        <Input size="large" placeholder="e.g. Best platform for career-recognized certificates, but not for casual budget learners." />
                      </Form.Item>
                    </div>

                    <div className="col-md-12">
                      <Form.Item label="In-Depth Editor's Review" name={["verdict", "editorNote"]}>
                        <TextArea rows={4} placeholder="Full editorial review analyzing course quality, interface, pricing fairness, and certificate worth..." />
                      </Form.Item>
                    </div>

                    <div className="col-md-12">
                      <Form.Item label="The Bottom Line (Final Recommendation)" name={["verdict", "bottomLine"]}>
                        <Input size="large" placeholder="e.g. If you want a degree or Google/IBM certificate, Coursera is unmatched. For single coding topics, check Udemy or Codecademy." />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </Tabs.TabPane>

              {/* TAB 3: Honest Pros & Cons */}
              <Tabs.TabPane
                tab={<span style={{ fontWeight: 600 }}><i className="fa fa-balance-scale text-primary me-1" /> Pros, Cons & The Catch</span>}
                key="3"
              >
                <div style={{ paddingTop: 16 }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <h5 style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#1e293b" }}>
                        Honest Pros & Cons Matrix
                      </h5>
                      <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                        Unfiltered strengths, critical flaws, and the hidden catch
                      </p>
                    </div>
                    <Button
                      type="primary"
                      ghost
                      size="small"
                      icon={<FaRobot />}
                      loading={generatingSection.prosAndCons}
                      onClick={() => handleGenerateSection("prosAndCons")}
                    >
                      AI Generate Pros & Cons
                    </Button>
                  </div>

                  <div className="row g-3">
                    {/* Pros */}
                    <div className="col-md-6">
                      <Card
                        size="small"
                        title={<span style={{ color: "#15803d", fontWeight: 600 }}><i className="fa fa-check-circle me-1" /> Top Strengths (Pros)</span>}
                        style={{ borderColor: "#bbf7d0", background: "#f0fdf4" }}
                      >
                        <Form.List name={["prosAndCons", "pros"]}>
                          {(fields, { add, remove }) => (
                            <>
                              {fields.map(({ key, name, ...restField }) => (
                                <div key={key} className="d-flex align-items-center gap-2 mb-2">
                                  <Form.Item {...restField} name={name} className="mb-0 flex-grow-1">
                                    <Input placeholder="e.g. University-backed credentials with strong employer recognition" />
                                  </Form.Item>
                                  <Button danger type="text" icon={<FaTrash />} onClick={() => remove(name)} />
                                </div>
                              ))}
                              <Button type="dashed" onClick={() => add()} block icon={<FaPlus />}>
                                Add Strength
                              </Button>
                            </>
                          )}
                        </Form.List>
                      </Card>
                    </div>

                    {/* Cons */}
                    <div className="col-md-6">
                      <Card
                        size="small"
                        title={<span style={{ color: "#b91c1c", fontWeight: 600 }}><i className="fa fa-times-circle me-1" /> Critical Drawbacks (Cons)</span>}
                        style={{ borderColor: "#fecaca", background: "#fef2f2" }}
                      >
                        <Form.List name={["prosAndCons", "cons"]}>
                          {(fields, { add, remove }) => (
                            <>
                              {fields.map(({ key, name, ...restField }) => (
                                <div key={key} className="d-flex align-items-center gap-2 mb-2">
                                  <Form.Item {...restField} name={name} className="mb-0 flex-grow-1">
                                    <Input placeholder="e.g. Free audit mode restricts graded assignments and certificates" />
                                  </Form.Item>
                                  <Button danger type="text" icon={<FaTrash />} onClick={() => remove(name)} />
                                </div>
                              ))}
                              <Button type="dashed" onClick={() => add()} block icon={<FaPlus />}>
                                Add Drawback
                              </Button>
                            </>
                          )}
                        </Form.List>
                      </Card>
                    </div>

                    {/* The Catch */}
                    <div className="col-md-12 mt-2">
                      <Card
                        size="small"
                        style={{ borderColor: "#fed7aa", background: "#fffbeb" }}
                        title={<span style={{ color: "#b45309", fontWeight: 700 }}>⚠️ The Catch (Hidden Caveats Platform Marketing Won't Tell You)</span>}
                      >
                        <Form.Item name={["prosAndCons", "theCatch"]} className="mb-0">
                          <TextArea
                            rows={3}
                            placeholder="e.g. While you can audit courses for free, you get zero graded feedback or certificates unless you pay. Degree programs also cost $10k+ and are not included in Plus."
                          />
                        </Form.Item>
                      </Card>
                    </div>
                  </div>
                </div>
              </Tabs.TabPane>

              {/* TAB 4: Learner Fit Matrix */}
              <Tabs.TabPane
                tab={<span style={{ fontWeight: 600 }}><i className="fa fa-users text-info me-1" /> Learner Fit Matrix</span>}
                key="4"
              >
                <div style={{ paddingTop: 16 }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <h5 style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#1e293b" }}>
                        Learner Persona Fit Assessment
                      </h5>
                      <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                        Helps learners immediately know if this platform suits their specific situation
                      </p>
                    </div>
                    <Button
                      type="primary"
                      ghost
                      size="small"
                      icon={<FaRobot />}
                      loading={generatingSection.learnerFit}
                      onClick={() => handleGenerateSection("learnerFit")}
                    >
                      AI Generate Fit Matrix
                    </Button>
                  </div>

                  <Form.List name="learnerFitProfiles">
                    {(fields, { add, remove }) => (
                      <div className="row g-3">
                        {fields.map(({ key, name, ...restField }) => (
                          <div key={key} className="col-md-6">
                            <Card size="small" style={{ borderRadius: 8, borderColor: "#e2e8f0" }}>
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <Form.Item {...restField} name={[name, "persona"]} className="mb-0 flex-grow-1 me-2">
                                  <Input placeholder="Persona (e.g. Career Changers)" style={{ fontWeight: 600 }} />
                                </Form.Item>
                                <div className="d-flex align-items-center gap-1">
                                  <span style={{ fontSize: "12px", color: "#64748b" }}>Fit:</span>
                                  <Form.Item {...restField} name={[name, "fitScore"]} className="mb-0">
                                    <InputNumber min={1} max={10} style={{ width: 60 }} />
                                  </Form.Item>
                                  <span style={{ fontSize: "11px", color: "#94a3b8" }}>/10</span>
                                  <Button danger type="text" size="small" icon={<FaTrash />} onClick={() => remove(name)} />
                                </div>
                              </div>
                              <Form.Item {...restField} name={[name, "reasoning"]} className="mb-0">
                                <TextArea rows={2} placeholder="Explain why it is or isn't a good fit for this persona..." />
                              </Form.Item>
                            </Card>
                          </div>
                        ))}
                        <div className="col-md-12">
                          <Button type="dashed" onClick={() => add({ persona: "", fitScore: 5, reasoning: "" })} block icon={<FaPlus />}>
                            Add Learner Persona
                          </Button>
                        </div>
                      </div>
                    )}
                  </Form.List>
                </div>
              </Tabs.TabPane>

              {/* TAB 5: Cost & Value Reality */}
              <Tabs.TabPane
                tab={<span style={{ fontWeight: 600 }}><i className="fa fa-dollar-sign text-success me-1" /> Cost & Economics</span>}
                key="5"
              >
                <div style={{ paddingTop: 16 }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <h5 style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#1e293b" }}>
                        Real Cost Breakdown & Break-Even Economics
                      </h5>
                      <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                        Transparent evaluation of subscriptions vs single-purchase economics
                      </p>
                    </div>
                    <Button
                      type="primary"
                      ghost
                      size="small"
                      icon={<FaRobot />}
                      loading={generatingSection.costAnalysis}
                      onClick={() => handleGenerateSection("costAnalysis")}
                    >
                      AI Generate Cost Analysis
                    </Button>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <Form.Item label="Free Tier Reality" name={["costAnalysis", "freeTierReality"]}>
                        <TextArea rows={3} placeholder="e.g. Free audit gives access to video lectures only. Quizzes and verified certificates require payment." />
                      </Form.Item>
                    </div>
                    <div className="col-md-6">
                      <Form.Item label="Best-Value Plan" name={["costAnalysis", "bestValuePlan"]}>
                        <TextArea rows={3} placeholder="e.g. Coursera Plus Annual ($399/yr) is by far the best option if taking 3+ courses." />
                      </Form.Item>
                    </div>
                    <div className="col-md-6">
                      <Form.Item label="Break-Even Calculations" name={["costAnalysis", "breakEvenNotes"]}>
                        <TextArea rows={3} placeholder="e.g. Single specializations cost $49/mo (typically 4 months = $196). Completing 2 specializations equals the annual subscription price." />
                      </Form.Item>
                    </div>
                    <div className="col-md-6">
                      <Form.Item label="Hidden Costs & Refund Nuances" name={["costAnalysis", "hiddenCosts"]}>
                        <TextArea rows={3} placeholder="e.g. Subscriptions auto-renew monthly. 14-day refund window on subscriptions, 7-day on individual courses." />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </Tabs.TabPane>

              {/* TAB 6: Credential Value */}
              <Tabs.TabPane
                tab={<span style={{ fontWeight: 600 }}><i className="fa fa-graduation-cap text-purple me-1" /> Certificate Value</span>}
                key="6"
              >
                <div style={{ paddingTop: 16 }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <h5 style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#1e293b" }}>
                        Certificate Value & Employer Recognition
                      </h5>
                      <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                        Does this certificate actually help on a resume or job interview?
                      </p>
                    </div>
                    <Button
                      type="primary"
                      ghost
                      size="small"
                      icon={<FaRobot />}
                      loading={generatingSection.certificateValue}
                      onClick={() => handleGenerateSection("certificateValue")}
                    >
                      AI Generate Credential Value
                    </Button>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-4">
                      <Form.Item label="Industry & Tech Recognition" name={["certificateValue", "industryRecognition"]}>
                        <TextArea rows={4} placeholder="e.g. Google and Meta professional certs are widely recognized for entry-level hiring." />
                      </Form.Item>
                    </div>
                    <div className="col-md-4">
                      <Form.Item label="Employer / Recruiter Acceptance" name={["certificateValue", "employerAcceptance"]}>
                        <TextArea rows={4} placeholder="e.g. High for entry-level roles and portfolio validation; will not replace a 4-year degree for senior roles." />
                      </Form.Item>
                    </div>
                    <div className="col-md-4">
                      <Form.Item label="LinkedIn Profile Impact" name={["certificateValue", "linkedInDisplayValue"]}>
                        <TextArea rows={4} placeholder="e.g. Direct 1-click verification link to LinkedIn Licenses & Certifications section." />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </Tabs.TabPane>

              {/* TAB 7: Competitors & FAQs */}
              <Tabs.TabPane
                tab={<span style={{ fontWeight: 600 }}><i className="fa fa-exchange-alt text-dark me-1" /> Competitors & FAQs</span>}
                key="7"
              >
                <div style={{ paddingTop: 16 }}>
                  {/* Competitor Decision Rules */}
                  <h5 style={{ fontWeight: 700, fontSize: "15px", color: "#1e293b", marginBottom: 12 }}>
                    Direction-Giver: Head-to-Head Competitor Decision Rules
                  </h5>

                  <Form.List name="competitorComparisons">
                    {(fields, { add, remove }) => (
                      <div className="row g-3 mb-4">
                        {fields.map(({ key, name, ...restField }) => (
                          <div key={key} className="col-md-12">
                            <Card size="small" style={{ borderRadius: 8, background: "#f8fafc", borderColor: "#e2e8f0" }}>
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <div className="d-flex align-items-center gap-2 flex-grow-1 me-2">
                                  <span style={{ fontWeight: 600, fontSize: "13px" }}>Compare with:</span>
                                  <Form.Item {...restField} name={[name, "competitorCode"]} className="mb-0" style={{ width: 220 }}>
                                    <Select placeholder="Select Competitor">
                                      {allPlatforms.map(p => (
                                        <Option key={p.code} value={p.code}>
                                          {p.name} ({p.code})
                                        </Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                </div>
                                <Button danger type="text" icon={<FaTrash />} onClick={() => remove(name)} />
                              </div>
                              <div className="row g-2">
                                <div className="col-md-6">
                                  <Form.Item
                                    {...restField}
                                    name={[name, "chooseIfThis"]}
                                    label={<span style={{ color: "#15803d" }}>Pick {platformData?.name || "This"} If:</span>}
                                    className="mb-0"
                                  >
                                    <Input placeholder="e.g. You need university accredited credentials and guided grading." />
                                  </Form.Item>
                                </div>
                                <div className="col-md-6">
                                  <Form.Item
                                    {...restField}
                                    name={[name, "chooseIfOther"]}
                                    label={<span style={{ color: "#2563eb" }}>Pick Competitor If:</span>}
                                    className="mb-0"
                                  >
                                    <Input placeholder="e.g. You only want a cheap $15 one-off tutorial for a specific tool." />
                                  </Form.Item>
                                </div>
                              </div>
                            </Card>
                          </div>
                        ))}
                        <div className="col-md-12">
                          <Button type="dashed" onClick={() => add()} block icon={<FaPlus />}>
                            Add Competitor Comparison
                          </Button>
                        </div>
                      </div>
                    )}
                  </Form.List>

                  {/* FAQs */}
                  <h5 style={{ fontWeight: 700, fontSize: "15px", color: "#1e293b", marginBottom: 12 }}>
                    Platform FAQ Accordion
                  </h5>
                  <Form.List name="faqs">
                    {(fields, { add, remove }) => (
                      <div className="row g-2">
                        {fields.map(({ key, name, ...restField }) => (
                          <div key={key} className="col-md-12">
                            <Card size="small" style={{ borderRadius: 8, borderColor: "#e2e8f0" }}>
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <Form.Item {...restField} name={[name, "question"]} className="mb-0 flex-grow-1 me-2">
                                  <Input placeholder="Question (e.g. Can I get a refund?)" style={{ fontWeight: 600 }} />
                                </Form.Item>
                                <Button danger type="text" icon={<FaTrash />} onClick={() => remove(name)} />
                              </div>
                              <Form.Item {...restField} name={[name, "answer"]} className="mb-0">
                                <TextArea rows={2} placeholder="Answer details..." />
                              </Form.Item>
                            </Card>
                          </div>
                        ))}
                        <div className="col-md-12">
                          <Button type="dashed" onClick={() => add({ question: "", answer: "", isActive: true })} block icon={<FaPlus />}>
                            Add FAQ
                          </Button>
                        </div>
                      </div>
                    )}
                  </Form.List>
                </div>
              </Tabs.TabPane>

              {/* TAB 8: SEO & Metadata */}
              <Tabs.TabPane
                tab={<span style={{ fontWeight: 600 }}><i className="fa fa-globe text-primary me-1" /> SEO & Meta</span>}
                key="8"
              >
                <div style={{ paddingTop: 16 }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <h5 style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#1e293b" }}>
                        Search Engine Optimization (SEO)
                      </h5>
                      <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                        Meta tags, Open Graph cards, and search keywords
                      </p>
                    </div>
                    <Button
                      type="primary"
                      ghost
                      size="small"
                      icon={<FaRobot />}
                      loading={generatingSection.seo}
                      onClick={() => handleGenerateSection("seo")}
                    >
                      AI Generate SEO
                    </Button>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-12">
                      <Form.Item label="Meta Title (Max 60 chars)" name={["seo", "metaTitle"]}>
                        <Input size="large" />
                      </Form.Item>
                    </div>
                    <div className="col-md-12">
                      <Form.Item label="Meta Description (Max 160 chars)" name={["seo", "metaDescription"]}>
                        <TextArea rows={2} />
                      </Form.Item>
                    </div>
                    <div className="col-md-6">
                      <Form.Item label="Open Graph (OG) Title" name={["seo", "ogTitle"]}>
                        <Input />
                      </Form.Item>
                    </div>
                    <div className="col-md-6">
                      <Form.Item label="Open Graph (OG) Description" name={["seo", "ogDescription"]}>
                        <Input />
                      </Form.Item>
                    </div>
                    <div className="col-md-12">
                      <Form.Item label="Target Keywords (comma-separated or chips)" name={["seo", "keywords"]}>
                        <Select mode="tags" style={{ width: "100%" }} placeholder="Add keywords..." />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </Tabs.TabPane>

            </Tabs>
          </Card>
        </Form>
      </div>

      {/* AI Settings Modal */}
      <AIGeneratorConfig
        visible={configModal.visible}
        category={configModal.category}
        defaultPrompt={configModal.defaultPrompt}
        contextInstructions={configModal.contextInstructions}
        hidePromptsTab={configModal.hidePromptsTab}
        onClose={() => setConfigModal(prev => ({ ...prev, visible: false }))}
      />
    </Page>
  );
}
